/**
 * PORTED — RO-3-00f / #44473.
 * Origin: `excellent/src/directory/views.tsx`.
 *
 * Cause A rewrite: the Next view read the vault in render
 * (`listPeople` / `listOrganizations` / `listWorkspaceActivity` /
 * `readEnrichedField` + self-heal). Those reads moved to
 * `directory.entities` / `directory.activity`. This file is a pure client
 * consumer via `useApi`. Navigation uses the router seam (never `next/*`).
 */

import {
  DataTable,
  Badge,
  Button,
  Card,
  formatDate,
  ActivityGroupList,
  ObjectLink,
} from "@excellent/ui";
import { Building2, Globe, MapPin, SquarePen } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import { directory } from "@excellent/ui/api/contracts/directory";

export function DirectoryCompanyCard({
  company,
}: {
  company: { name: string | null; hq: string | null; domain: string | null };
}) {
  const { name, hq, domain } = company;
  const hasProfile = Boolean(name || hq || domain);
  const domainHref = domain
    ? domain.startsWith("http")
      ? domain
      : `https://${domain}`
    : null;

  return (
    <Card className="px-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-muted-foreground">
            <Building2 className="size-4" />
          </span>
          <div className="space-y-1">
            <p className="type-subtext text-muted-foreground">Your company</p>
            {hasProfile ? (
              <>
                <p className="type-body font-medium text-foreground">
                  {name ?? "Your organization"}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 type-body text-muted-foreground">
                  {domain && domainHref ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Globe className="size-3.5 shrink-0" />
                      <a
                        href={domainHref}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {domain}
                      </a>
                    </span>
                  ) : null}
                  {hq ? (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="size-3.5 shrink-0" />
                      {hq}
                    </span>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="type-body text-muted-foreground">
                Add your company details so the workspace knows who you are.
              </p>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link href="/settings/company-profile">
            <SquarePen className="size-3.5" />
            {hasProfile ? "Edit" : "Set up"}
          </Link>
        </Button>
      </div>
    </Card>
  );
}

/** People + organizations — the canonical entity reference for a workspace. */
export function DirectoryEntities({
  workspaceId,
  mode,
}: {
  workspaceId: number;
  mode?: string;
}) {
  const { data, error, isLoading } = useApi(directory.entities, {
    query: { workspaceId },
  });

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading directory…</p>;
  }
  if (error || !data) {
    return (
      <p className="type-body text-destructive">
        {error?.message ?? "Could not load directory."}
      </p>
    );
  }

  const { people, organizations, company } = data;

  return (
    <div className="space-y-4">
      <DirectoryCompanyCard company={company} />
      <DataTable
        title="People"
        count={people.length}
        rows={people}
        rowKey={(r) => r.id}
        empty="No people yet. Candidates (Hiring) and contacts (Sales) appear here as they're created."
        columns={[
          {
            key: "name",
            header: "Name",
            render: (r) => (
              <span className="inline-flex items-center gap-2">
                <ObjectLink
                  uid={r.uid}
                  from={mode}
                  className="font-medium hover:underline"
                >
                  {r.display_name}
                </ObjectLink>
                {r.is_self ? <Badge variant="secondary">You</Badge> : null}
              </span>
            ),
          },
          {
            key: "given",
            header: "Given / family",
            render: (r) =>
              [r.given_name, r.family_name].filter(Boolean).join(" ") || "—",
          },
          {
            key: "actor",
            header: "Linked actor",
            render: (r) =>
              r.actor_id ? (
                <Badge variant="outline">actor #{r.actor_id}</Badge>
              ) : (
                "—"
              ),
          },
          {
            key: "created",
            header: "Added",
            render: (r) => formatDate(r.created_at),
          },
        ]}
      />

      <DataTable
        title="Organizations"
        count={organizations.length}
        rows={organizations}
        rowKey={(r) => r.id}
        empty="No organizations yet."
        columns={[
          {
            key: "name",
            header: "Name",
            render: (r) => (
              <span className="inline-flex items-center gap-2">
                <ObjectLink
                  uid={r.uid}
                  from={mode}
                  className="font-medium hover:underline"
                >
                  {r.name}
                </ObjectLink>
                {r.is_self ? (
                  <Badge variant="secondary">Your company</Badge>
                ) : null}
              </span>
            ),
          },
          {
            key: "legal",
            header: "Legal name",
            render: (r) => r.legal_name ?? "—",
          },
          {
            key: "website",
            header: "Website",
            render: (r) => r.website ?? "—",
          },
          {
            key: "created",
            header: "Added",
            render: (r) => formatDate(r.created_at),
          },
        ]}
      />
    </div>
  );
}

/** Cross-product workspace activity. */
export function DirectoryActivity({ workspaceId }: { workspaceId: number }) {
  const { data, error, isLoading } = useApi(directory.activity, {
    query: { workspaceId, limit: 250 },
  });

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading activity…</p>;
  }
  if (error || !data) {
    return (
      <p className="type-body text-destructive">
        {error?.message ?? "Could not load activity."}
      </p>
    );
  }

  return (
    <ActivityGroupList
      // #44545: directory.activity contract now validates ActivityGroup[] — no cast.
      groups={data.groups}
      title="Recent activity"
      emptyBody="No activity recorded yet."
    />
  );
}
